import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LineChart, Line, ResponsiveContainer } from "recharts";

const FONT_MONO = { fontFamily: "'IBM Plex Mono', monospace" };

function useCountUp(target, duration = 800) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let start = null;
    const from = 0;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setValue(from + (target - from) * progress);
      if (progress < 1) requestAnimationFrame(step);
    };
    const raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

export const StatCard = ({ testId, label, value, suffix = "", accent = "amber", sparkline, decimals = 0 }) => {
  const animated = useCountUp(Number(value) || 0);
  const accentColor = { amber: "#FBBF24", emerald: "#34D399", indigo: "#818CF8", rose: "#F87171" }[accent] || "#FBBF24";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      data-testid={testId}
      className="bg-slate-900/80 border border-slate-800 rounded-xl p-5 shadow-lg backdrop-blur-md transition-all duration-200 hover:border-slate-700"
    >
      <p className="text-xs font-mono text-slate-400 tracking-wider uppercase mb-2">{label}</p>
      <div className="flex items-end justify-between gap-3">
        <p style={FONT_MONO} className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-50">
          {animated.toFixed(decimals)}{suffix}
        </p>
        {sparkline && sparkline.length > 1 && (
          <div style={{ width: 72, height: 32 }}>
            <ResponsiveContainer minWidth={40} minHeight={20}>
              <LineChart data={sparkline}>
                <Line type="monotone" dataKey="count" stroke={accentColor} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </motion.div>
  );
};
